import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Anchor,
  Badge,
  Code,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { DataTable } from 'mantine-datatable';
import { IconArrowLeft, IconSearch } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useDocumentAccessLog } from '../../queries/documents';
import { useProjectStore } from '../../project/project-store';
import { formatAccessLogRow } from './access-log-format';
import type { DocumentAccessAction } from '../../types/api';

const ACTION_OPTIONS: { value: DocumentAccessAction | ''; label: string }[] = [
  { value: '', label: 'All actions' },
  { value: 'preview', label: 'Preview' },
  { value: 'download', label: 'Download' },
  { value: 'archive', label: 'Archive' },
];

export function DocumentsActivityPage() {
  const projectId = useProjectStore((s) => s.projectId);
  const [action, setAction] = useState<DocumentAccessAction | ''>('');
  const [q, setQ] = useState('');
  const [since, setSince] = useState<string | null>(null);
  const [until, setUntil] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      action: (action || undefined) as DocumentAccessAction | undefined,
      q: q.trim() || undefined,
      // The DateInput onChange returns YYYY-MM-DD; convert to ISO timestamps
      // so the API's z.string().datetime() validator accepts it. Until is
      // exclusive end-of-day so a "today" filter includes today.
      since: since ? `${since}T00:00:00.000Z` : undefined,
      until: until ? dayjs(until).add(1, 'day').toISOString() : undefined,
      limit: 200,
    }),
    [action, q, since, until],
  );

  const { data: entries = [], isLoading } = useDocumentAccessLog(filters);

  if (!projectId) {
    return (
      <Stack>
        <Title order={2}>Activity</Title>
        <Text c="dimmed">Pick a project to view its document activity.</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Anchor component={Link} to="/documents" size="sm" c="dimmed">
          <IconArrowLeft size={14} /> Documents
        </Anchor>
        <Title order={2}>Document activity</Title>
        <Text c="dimmed" size="sm">
          Every preview, download, and archive request for this project. Sorted newest first.
        </Text>
      </Stack>

      <Group>
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search filename, username, email"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          w={300}
        />
        <Select
          data={ACTION_OPTIONS}
          value={action}
          onChange={(v) => setAction((v as DocumentAccessAction) ?? '')}
          w={160}
          allowDeselect={false}
        />
        <DateInput
          placeholder="Since"
          value={since}
          onChange={(v) => setSince(v ?? null)}
          valueFormat="YYYY-MM-DD"
          clearable
          w={140}
        />
        <DateInput
          placeholder="Until"
          value={until}
          onChange={(v) => setUntil(v ?? null)}
          valueFormat="YYYY-MM-DD"
          clearable
          w={140}
        />
      </Group>

      <DataTable
        withTableBorder
        highlightOnHover
        minHeight={150}
        fetching={isLoading}
        records={entries}
        idAccessor="id"
        columns={[
          {
            accessor: 'created_at',
            title: 'When',
            width: 160,
            render: (e) => dayjs(e.created_at).format('YYYY-MM-DD HH:mm:ss'),
          },
          {
            accessor: 'action',
            title: 'Action',
            width: 110,
            render: (e) => {
              const f = formatAccessLogRow(e, () => '');
              return (
                <Badge variant="light" color={f.actionColor} tt="lowercase">
                  {f.actionLabel}
                </Badge>
              );
            },
          },
          {
            accessor: 'who',
            title: 'Who',
            render: (e) => {
              const f = formatAccessLogRow(e, () => '');
              return e.username ? (
                <Stack gap={0}>
                  <Text size="sm">{f.whoLabel}</Text>
                  <Text size="xs" c="dimmed">
                    {f.whoSubLabel}
                  </Text>
                </Stack>
              ) : (
                <Text c="dimmed">{f.whoLabel}</Text>
              );
            },
          },
          {
            accessor: 'document',
            title: 'Document',
            render: (e) => {
              const f = formatAccessLogRow(e, () => '');
              if (e.action === 'archive' || !e.original_filename) {
                return <Text c="dimmed">{f.documentLabel}</Text>;
              }
              return (
                <Stack gap={0}>
                  <Text size="sm">{f.documentLabel}</Text>
                  {e.content_type && (
                    <Code fz="xs" c="dimmed">
                      {e.content_type}
                    </Code>
                  )}
                </Stack>
              );
            },
          },
          {
            accessor: 'ip_address',
            title: 'IP',
            width: 140,
            render: (e) => e.ip_address ?? <Text c="dimmed">—</Text>,
          },
        ]}
      />
    </Stack>
  );
}
